import React, {
  useMemo,
  useCallback,
} from "react";

import {
  Box,
  createColumnHelper,
  DataTable,
  Text,
  type DataTableColumnDef,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";
import { IssueViewContext } from "#app/components/issue-view";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { DashboardActivityDrilldownDocument, type DashboardDrilldownActivity } from "#app/lib/data";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { AgeReasonGroupSummary } from "./AgeReasonDrilldown";
import { useDrilldownGroups } from "../hooks/use-drilldown-groups";
import {
  DrilldownGroupTitle,
  DrilldownIssueLabel,
  DrilldownMemberTag,
} from "./DrilldownGroupTitle";
import type { Omittable } from "@salestrak/ui";

const GROUP_PAGE_SIZE = 50;

// ── Row type ─────────────────────────────────────────────────────────────────

type ActivityRow = {
  id: string;
  _issueId: string;
  _creationDateRaw: string;
  project: string;
  reason: string;
  time: string;
  address: string;
};

const ph = createColumnHelper<ActivityRow>();

// ── Group title ──────────────────────────────────────────────────────────────

const GoldenHourGroupTitle = React.memo(function GoldenHourGroupTitle({
  userId,
  name,
  count,
  onVisible,
}: Omittable<{
  userId: string;
  name: string;
  count: number;
  onVisible?: () => void;
}>) {
  return (
    <DrilldownGroupTitle onVisible={onVisible}>
      <Box direction="row" gap="xs" align="center">
        <DrilldownMemberTag userId={userId} name={name} />
        <Text size="xs" variant="muted">
          {count} {count === 1 ? "update" : "updates"}
        </Text>
      </Box>
    </DrilldownGroupTitle>
  );
});

// ── Props ─────────────────────────────────────────────────────────────────────

export type GoldenHourQueryInput = Omittable<{
  startDate: string;
  endDate: string;
  teamIds?: string[];
  memberIds?: string[];
  hour: number;
  timezone: string;
  sortField?: string;
  sortDirection?: string;
}>;

interface GoldenHourDrilldownProps {
  summaries: AgeReasonGroupSummary[];
  queryInput: GoldenHourQueryInput;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function GoldenHourDrilldown({
  summaries,
  queryInput,
  sorting,
  onSortingChange,
}: GoldenHourDrilldownProps) {
  const issueViewCtx = React.useContext(IssueViewContext);
  const { registerState, getGroupProps } = useGroupStates<DashboardDrilldownActivity>();
  const summaryKeys = useMemo(() => summaries.map((s) => s.userId), [summaries]);
  const { activatedGroupKeys, activateGroup } = useDrilldownGroups(summaryKeys);

  const openIssue = useCallback(
    (row: ActivityRow) => {
      if (row._issueId) issueViewCtx?.openIssueById(row._issueId);
    },
    [issueViewCtx],
  );

  const sort = sorting[0];


  const getConnection = useCallback(
    (data: any) => data?.dashboardActivityDrilldown,
    [],
  );

  // The drilldown row is a CLIENT-ONLY aggregate, not a server noun: it
  // collapses and re-sorts the page in memory. It is built here, where the
  // row type is declared, never inside the data layer.
  const toRows = useCallback(
    (nodes: readonly DashboardDrilldownActivity[]) => {
      const rows: ActivityRow[] = nodes.map((a) => {
        const raw = a.reasonName ?? a.action;
        const reason =
          raw && raw.toLowerCase() !== "updated" ? raw : "—";
        return {
          id: a.id,
          _issueId: a.issueId,
          _creationDateRaw: a.creationDate,
          project: a.issueName ?? `#${a.issueId}`,
          reason,
          time: new Date(a.creationDate).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
          }),
          address: a.issueAddress ?? "—",
        };
      });

      if (sort) {
        const { id: sortKey, desc } = sort;
        const dir = desc ? -1 : 1;

        rows.sort((a, b) => {
          let cmp = 0;
          if (sortKey === "time") {
            cmp = a._creationDateRaw.localeCompare(b._creationDateRaw);
          } else {
            cmp = String(
              a[sortKey as keyof ActivityRow] ?? "",
            ).localeCompare(
              String(b[sortKey as keyof ActivityRow] ?? ""),
            );
          }
          return cmp * dir;
        });
      } else {
        rows.sort((a, b) =>
          b._creationDateRaw.localeCompare(a._creationDateRaw),
        );
      }

      return rows;
    },
    [sort],
  );

  const groups = useMemo<DataTableGroupType<ActivityRow>[]>(() => {
    return summaries.map((summary) => ({
      key: summary.userId,
      label: summary.name,
      totalCount: summary.totalUpdates,
      ...getGroupProps(summary.userId),
      data: toRows(getGroupProps(summary.userId).data),
    }));
  }, [summaries, getGroupProps, toRows]);

  const summaryByUserId = useMemo(() => {
    const map = new Map<string, AgeReasonGroupSummary>();
    for (const s of summaries) map.set(s.userId, s);
    return map;
  }, [summaries]);

  const groupTitle = useCallback(
    (groupKey: string) => {
      const summary = summaryByUserId.get(groupKey);
      if (!summary) return null;
      return (
        <GoldenHourGroupTitle
          userId={summary.userId}
          name={summary.name}
          count={summary.totalUpdates}
          onVisible={() => activateGroup(groupKey)}
        />
      );
    },
    [activateGroup, summaryByUserId],
  );

  const tableView = useTableViewSettings("drilldown:goldenHour");

  const columns = useMemo<DataTableColumnDef<ActivityRow>[]>(
    () => [
      ph.hoverAction({
        accessorKey: "project",
        header: "Project",
        meta: { minWidth: 220 },
        content: (row) => (
          <DrilldownIssueLabel issueId={row._issueId} name={row.project} />
        ),
        onClickAction: (row) => openIssue(row),
        actionLabel: (row) => `Open ${row.project}`,
      }),
      ph.accessor({
        accessorKey: "reason",
        header: "Reason",
        enableSorting: true,
        meta: { minWidth: 200 },
      }),
      ph.accessor({
        accessorKey: "time",
        header: "Time",
        enableSorting: true,
      }),
      ph.accessor({
        accessorKey: "address",
        header: "Address",
        enableSorting: true,
        meta: { minWidth: 350 },
      }),
    ],
    [openIssue],
  );

  return (
    <>
      {summaries
        .filter((summary) => activatedGroupKeys.has(summary.userId))
        .map((summary) => (
          <GroupQueryProvider<DashboardDrilldownActivity>
            key={summary.userId}
            groupKey={summary.userId}
            document={DashboardActivityDrilldownDocument}
            variables={{
              input: {
                ...queryInput,
                subject: "age",
                userId: summary.userId,
              },
              limit: GROUP_PAGE_SIZE,
            }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ))}

      <DataTable
        columns={columns}
        groups={groups}
        groupTitle={groupTitle}
        getRowId={(r) => r.id}
        density="compact"
        fillColumnId="project"
        pinnedColumnIds={["project"]}
        stickyHeader
        hideGroupCount
        paddingX="md"
        sorting={sorting}
        onSortingChange={onSortingChange}
        {...tableView.tableProps}
      />
    </>
  );
}
